/**
 * OwnershipReviewManager — AdminHub "Ownership Review" tab.
 *
 * Queue (status filter, default `needs_review`) + detail panel:
 *   - document viewer (iframe / img on the admin stream endpoint; each load is
 *     audit-logged server-side as `document_viewed`)
 *   - extracted fields vs listing / seller, side by side
 *   - reasons and red flags as readable labels
 *   - the reviewer checklist from legal-review.md §3
 *   - decisions: approve / reject / request more info / revoke. A note is
 *     required for everything but approve; reject confirms first and says the
 *     Full Service fee will be refunded; a refund failure is shown loudly.
 *
 * Contract: docs/plans/ownership-verification/README.md § API → Admin.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import OwnershipGateSettings from './OwnershipGateSettings';
import { ownershipVerificationAgent } from '../../agents/ownershipVerificationAgent';
import {
  ADMIN_STATUS_FILTERS,
  ADMIN_STATUS_LABELS,
  CAPACITY_LABELS,
  REVIEWER_CHECKLIST,
  REVIEWER_GENERAL_GUIDANCE,
  REVIEWER_RED_FLAGS,
  documentTypeLabel,
  eventLabel,
  reasonLabel,
  redFlagLabel,
} from '../../utils/ownershipVerification';
import styles from './OwnershipReviewManager.module.css';

const DECISIONS = {
  approve: { label: 'Approve', noteRequired: false, tone: 'approve' },
  request_info: { label: 'Request more info', noteRequired: true, tone: 'neutral' },
  reject: { label: 'Reject', noteRequired: true, tone: 'danger' },
  revoke: { label: 'Revoke', noteRequired: true, tone: 'danger' },
  // rejected → more_info_requested; the refund stays as history.
  reopen: { label: 'Reopen', noteRequired: true, tone: 'neutral' },
};

export const REOPEN_CONFIRM_TEXT =
  "The seller will be asked for more information. If billing is on, they'll need to pay the listing fee again before publishing.";

/**
 * Whether the listing has been live before. Rejecting such a listing only
 * removes the buyer badge — no refund, no unpublish (legal, 2026-09-29).
 *
 * The backend sends `listing: {first_published_at, listing_status, ...}`;
 * top-level copies are accepted as a fallback.
 */
export function wasPreviouslyPublished(detail) {
  if (!detail) return false;
  const firstPublishedAt = detail.listing?.first_published_at ?? detail.first_published_at;
  if (firstPublishedAt) return true;
  const listingStatus = detail.listing?.listing_status ?? detail.listing_status;
  return Boolean(listingStatus) && listingStatus !== 'draft';
}

/** Readable `revoke_source` (shown when status is revoked). */
export const REVOKE_SOURCE_LABELS = {
  admin: 'Revoked by an admin',
  address_change: 'Revoked automatically: listing address changed',
  name_change: 'Revoked automatically: account name changed',
};

/**
 * Title for a queue item / detail. `house_id` is null once the listing has
 * been deleted (the verification record outlives it).
 */
export function listingTitle(item) {
  if (!item || item.house_id == null) return 'Listing deleted';
  return item.address || `House #${item.house_id}`;
}

/** Seller line; `seller_id` is null once the account has been deleted. */
export function sellerText(item) {
  if (!item) return '—';
  const who = [item.seller_name, item.seller_email].filter(Boolean).join(' · ');
  if (who) return who;
  return item.seller_id === null ? 'Account deleted' : 'Unknown seller';
}

/** Which decisions the contract allows from a status. */
export function allowedActions(status) {
  if (status === 'needs_review' || status === 'more_info_requested') {
    return ['approve', 'request_info', 'reject'];
  }
  if (status === 'auto_approved' || status === 'approved') return ['revoke'];
  if (status === 'rejected') return ['reopen'];
  return [];
}

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMoney = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '—';
  return `$${Math.round(v).toLocaleString('en-US')}`;
};

const formatAddress = (a) => {
  if (!a || typeof a !== 'object') return '—';
  const line2 = [a.city, [a.state, a.zip || a.zip_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const text = [a.street || a.address, line2].filter(Boolean).join(', ');
  return text || '—';
};

/** `listing.ai_price_estimate` may be a number or `{low, recommended, high}`. */
const formatEstimate = (est) => {
  if (est == null) return '—';
  if (typeof est === 'number') return formatMoney(est);
  if (typeof est === 'object') {
    const mid = est.recommended ?? est.midpoint;
    const range =
      est.low && est.high ? ` (${formatMoney(est.low)} – ${formatMoney(est.high)})` : '';
    return `${formatMoney(mid)}${range}`;
  }
  return String(est);
};

const isImageDoc = (doc) => {
  const type = (doc?.content_type || '').toLowerCase();
  if (type) return type.startsWith('image/');
  return /\.(jpe?g|png)$/i.test(doc?.original_filename || '');
};

/** MIME type for a fetched document: the Blob's, else the record's, else by extension. */
const blobTypeFor = (blob, doc) => {
  if (blob?.type) return blob.type;
  if (doc?.content_type) return doc.content_type;
  if (/\.png$/i.test(doc?.original_filename || '')) return 'image/png';
  if (/\.jpe?g$/i.test(doc?.original_filename || '')) return 'image/jpeg';
  return 'application/pdf';
};

/**
 * Load one document into an object URL (see adminFetchDocument for why the
 * stream URL is never navigated to). Revokes the URL when the document changes
 * or the viewer unmounts. `reloadKey` bumps to retry.
 */
function useDocumentBlob(verificationId, doc, reloadKey) {
  const [state, setState] = useState({ loading: false, error: null, purged: false, url: null, type: null });
  const docId = doc?.id ?? null;
  const skip = !doc || Boolean(doc.purged_at);

  useEffect(() => {
    if (skip || docId == null) {
      setState({ loading: false, error: null, purged: false, url: null, type: null });
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    setState({ loading: true, error: null, purged: false, url: null, type: null });
    (async () => {
      try {
        const blob = await ownershipVerificationAgent.adminFetchDocument(verificationId, docId, {
          signal: controller ? controller.signal : undefined,
        });
        if (cancelled) return;
        const type = blobTypeFor(blob, doc);
        const typed = blob.type ? blob : new Blob([blob], { type });
        objectUrl = URL.createObjectURL(typed);
        setState({ loading: false, error: null, purged: false, url: objectUrl, type });
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        console.error('Ownership document load failed:', err?.status);
        setState({
          loading: false,
          purged: err?.status === 410,
          error: err?.status === 410 ? null : err?.message || 'Could not load this document.',
          url: null,
          type: null,
        });
      }
    })();
    return () => {
      cancelled = true;
      if (controller) controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // doc is read for its type only; docId/skip identify the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationId, docId, skip, reloadKey]);

  return state;
}

const scoreText = (n) => (typeof n === 'number' ? `${Math.round(n)} / 100` : '—');

function StatusChip({ status }) {
  return (
    <span className={`${styles.chip} ${styles[`chip_${status}`] || ''}`}>
      {ADMIN_STATUS_LABELS[status] || status || '—'}
    </span>
  );
}

// ── Document viewer ─────────────────────────────────────────────────────
function DocumentViewer({ verificationId, documents }) {
  const docs = Array.isArray(documents) ? documents : [];
  const [activeId, setActiveId] = useState(docs[0]?.id ?? null);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    setActiveId(docs[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationId]);
  const active = docs.find((d) => d.id === activeId) || docs[0] || null;
  const blob = useDocumentBlob(verificationId, active, reloadKey);

  if (!active) {
    return <p className={styles.muted}>No documents on file.</p>;
  }
  const purged = Boolean(active.purged_at) || blob.purged;
  const showImage = blob.type ? blob.type.startsWith('image/') : isImageDoc(active);
  const title = `${active.kind === 'authority' ? 'Authority' : 'Proof'} document: ${documentTypeLabel(active.document_type)}`;

  return (
    <div className={styles.viewer}>
      <div className={styles.docTabs} role="tablist" aria-label="Submitted documents">
        {docs.map((d) => (
          <button
            key={d.id}
            type="button"
            role="tab"
            aria-selected={d.id === active.id}
            className={`${styles.docTab} ${d.id === active.id ? styles.docTabActive : ''}`}
            onClick={() => setActiveId(d.id)}
          >
            <span className={styles.docTabKind}>{d.kind === 'authority' ? 'Authority' : 'Proof'}</span>
            {documentTypeLabel(d.document_type)}
          </button>
        ))}
      </div>
      <div className={styles.docMeta}>
        <span>{active.original_filename || 'Unnamed file'}</span>
        {active.uploaded_at && <span>Uploaded {formatDateTime(active.uploaded_at)}</span>}
        {blob.url && (
          <a
            href={blob.url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
            data-testid="ownership-doc-open"
          >
            Open in new tab
          </a>
        )}
      </div>
      {purged ? (
        <p className={styles.purged} role="status" data-testid="ownership-doc-purged">
          This document was deleted under the retention policy.
          {active.purged_at ? ` (Deleted ${formatDateTime(active.purged_at)}.)` : ''}
        </p>
      ) : blob.loading || (!blob.url && !blob.error) ? (
        <p className={`${styles.docFrame} ${styles.docPlaceholder}`} role="status" data-testid="ownership-doc-loading">
          Loading document…
        </p>
      ) : blob.error ? (
        <div className={`${styles.docFrame} ${styles.docPlaceholder}`} role="alert" data-testid="ownership-doc-error">
          <p>{blob.error}</p>
          <button type="button" className={styles.secondaryBtn} onClick={() => setReloadKey((k) => k + 1)}>
            Try again
          </button>
        </div>
      ) : blob.url ? (
        showImage ? (
          <img className={styles.docImage} src={blob.url} alt={title} data-testid="ownership-doc-image" />
        ) : (
          <iframe className={styles.docFrame} src={blob.url} title={title} data-testid="ownership-doc-frame" />
        )
      ) : null}
    </div>
  );
}

// ── Extracted vs listing ────────────────────────────────────────────────
function ComparisonTable({ detail }) {
  const ex = detail.extracted || null;
  const match = detail.match_result || {};
  const proof = (detail.documents || []).find((d) => d.kind === 'proof');
  const listingAddress = {
    address: detail.address,
    city: detail.city,
    state: detail.state,
    zip_code: detail.zip_code ?? detail.listing?.zip_code,
  };
  const others = Array.isArray(detail.other_owners) ? detail.other_owners : [];
  const rows = [
    {
      field: 'Owner(s)',
      doc: ex?.owner_names?.length ? ex.owner_names.join('; ') : '—',
      ours: [detail.seller_name, ...others].filter(Boolean).join('; ') || '—',
      note: `Name score ${scoreText(match.name_score)}`,
    },
    {
      field: 'Property address',
      doc: formatAddress(ex?.property_address),
      ours: formatAddress(listingAddress),
      note: `Address score ${scoreText(match.address_score)}`,
    },
    {
      field: 'Document type',
      doc: ex ? documentTypeLabel(ex.document_type) : '—',
      ours: proof ? `${documentTypeLabel(proof.document_type)} (declared)` : '—',
    },
    { field: 'Parcel number (APN)', doc: ex?.apn || match.apn || '—', ours: '—' },
    { field: 'Document date', doc: ex?.document_date || '—', ours: '—' },
    {
      field: 'Recorded',
      doc: ex?.recording
        ? `${ex.recording.recorded ? 'Yes' : 'No'}${ex.recording.document_number ? ` · #${ex.recording.document_number}` : ''}`
        : '—',
      ours: '—',
    },
    {
      field: 'Owner mailing address',
      doc: formatAddress(ex?.owner_mailing_address),
      ours: detail.state ? `Listing state: ${detail.state}` : '—',
    },
    {
      field: 'Extraction confidence',
      doc: typeof ex?.confidence === 'number' ? `${Math.round(ex.confidence * 100)}%` : '—',
      ours: '',
    },
  ];

  return (
    <div className={styles.tableWrap}>
      {!ex && (
        <p className={styles.warnInline}>
          No extracted data — automated reading failed or hasn&apos;t run. Read the document directly.
        </p>
      )}
      <table className={styles.compare}>
        <caption className={styles.visuallyHidden}>Extracted document fields compared with the listing and seller</caption>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">From the document</th>
            <th scope="col">Listing / seller</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field}>
              <th scope="row">
                {r.field}
                {r.note && <span className={styles.rowNote}>{r.note}</span>}
              </th>
              <td>{r.doc}</td>
              <td>{r.ours}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">Asking price vs AI estimate</th>
            <td>{formatEstimate(detail.listing?.ai_price_estimate)}</td>
            <td>{formatMoney(detail.listing?.price)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ReviewerChecklist() {
  return (
    <details className={styles.checklist} open>
      <summary className={styles.checklistSummary}>Reviewer checklist</summary>
      <ul className={styles.guidance}>
        {REVIEWER_GENERAL_GUIDANCE.map((g) => <li key={g}>{g}</li>)}
      </ul>
      <div className={styles.tableWrap}>
        <table className={styles.checkTable}>
          <thead>
            <tr>
              <th scope="col">Situation</th>
              <th scope="col">Ask for</th>
              <th scope="col">Check</th>
            </tr>
          </thead>
          <tbody>
            {REVIEWER_CHECKLIST.map((row) => (
              <tr key={row.situation}>
                <th scope="row">{row.situation}</th>
                <td>{row.askFor}</td>
                <td>{row.check}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.redFlagNote}>{REVIEWER_RED_FLAGS}</p>
    </details>
  );
}

// ── Decision form ───────────────────────────────────────────────────────
function DecisionPanel({ detail, onDecided }) {
  const uid = useId();
  const actions = allowedActions(detail.status);
  const [action, setAction] = useState(actions[0] || null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);

  useEffect(() => {
    setAction(allowedActions(detail.status)[0] || null);
    setNote('');
    setError(null);
  }, [detail.id, detail.status]);

  if (actions.length === 0) {
    return (
      <p className={styles.muted} data-testid="ownership-no-actions">
        No decision is available for a verification that is {ADMIN_STATUS_LABELS[detail.status]?.toLowerCase() || detail.status}.
      </p>
    );
  }

  const noteRequired = Boolean(action && DECISIONS[action].noteRequired);
  const noteMissing = noteRequired && !note.trim();

  const send = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await ownershipVerificationAgent.adminDecide(detail.id, { action, note });
      onDecided(result, action);
    } catch (err) {
      console.error('Ownership decision failed:', err?.status);
      setError(err?.message || 'The decision could not be saved.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // The button is disabled in this state; this guards Enter-key submits too.
    if (!action || noteMissing || submitting) return;
    if (action === 'reject') {
      setConfirmReject(true);
      return;
    }
    send();
  };

  return (
    <form className={styles.decision} onSubmit={handleSubmit} data-testid="ownership-decision-form">
      <fieldset className={styles.actionGroup}>
        <legend className={styles.label}>Decision</legend>
        <div className={styles.actionRow}>
          {actions.map((a) => (
            <label
              key={a}
              className={`${styles.actionOption} ${styles[`tone_${DECISIONS[a].tone}`]} ${action === a ? styles.actionOptionActive : ''}`}
            >
              <input
                type="radio"
                name={`${uid}-action`}
                value={a}
                checked={action === a}
                onChange={() => setAction(a)}
              />
              {DECISIONS[a].label}
            </label>
          ))}
        </div>
      </fieldset>
      <label className={styles.label} htmlFor={`${uid}-note`}>
        Note {noteRequired ? '(required — the seller sees this for "Request more info" and "Reject")' : '(optional)'}
      </label>
      <textarea
        id={`${uid}-note`}
        className={styles.textarea}
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        aria-required={noteRequired ? 'true' : undefined}
        aria-describedby={noteMissing ? `${uid}-note-err` : undefined}
        data-testid="ownership-decision-note"
      />
      {noteMissing && (
        <p className={styles.fieldError} id={`${uid}-note-err`} data-testid="ownership-note-required">
          A note is required for this decision.
        </p>
      )}
      {error && <p className={styles.errorBox} role="alert">{error}</p>}
      <button
        type="submit"
        className={`${styles.primaryBtn} ${action ? styles[`btn_${DECISIONS[action].tone}`] : ''}`}
        disabled={submitting || !action || noteMissing}
        aria-describedby={noteMissing ? `${uid}-note-err` : undefined}
        data-testid="ownership-decision-submit"
      >
        {submitting ? 'Saving…' : action ? DECISIONS[action].label : 'Choose a decision'}
      </button>
      {action === 'reopen' && (
        <p className={styles.muted} data-testid="ownership-reopen-confirm">{REOPEN_CONFIRM_TEXT}</p>
      )}
      {action === 'reject' && wasPreviouslyPublished(detail) && (
        <p className={styles.muted} data-testid="ownership-reject-published-hint">
          This listing has been published before: rejecting removes the badge only (no refund, no unpublish).
        </p>
      )}

      <Modal
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        title="Reject ownership verification?"
        size="sm"
      >
        <div data-testid="ownership-reject-confirm">
          {wasPreviouslyPublished(detail) ? (
            <p className={styles.confirmText} data-testid="ownership-reject-confirm-published">
              The seller&apos;s listing stays published, but the &quot;Ownership documents
              reviewed&quot; badge is removed. No refund is issued.
            </p>
          ) : (
            <p className={styles.confirmText} data-testid="ownership-reject-confirm-unpublished">
              The listing will not be published. Every Full Service fee paid for this listing
              will be refunded to the seller in full, automatically.
            </p>
          )}
          <p className={styles.confirmText}>
            The seller will see your note. Say only what was missing or didn&apos;t match.
            Don&apos;t accuse the seller of fraud or give legal advice (for example, about
            who must sign).
          </p>
          <div className={styles.confirmActions}>
            <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmReject(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.primaryBtn} ${styles.btn_danger}`}
              onClick={() => {
                setConfirmReject(false);
                send();
              }}
              data-testid="ownership-reject-confirm-btn"
            >
              Reject and refund
            </button>
          </div>
        </div>
      </Modal>
    </form>
  );
}

function RefundNotice({ refund }) {
  // 'not_applicable': nothing to refund (e.g. a previously-published listing).
  if (!refund || !refund.status || refund.status === 'none' || refund.status === 'not_applicable') return null;
  if (refund.status === 'failed') {
    const detail = refund.detail;
    const detailText =
      typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : null;
    return (
      <div className={styles.refundFailed} role="alert" data-testid="ownership-refund-failed">
        <strong>Refund failed.</strong> The rejection was saved, but the Full Service fee was not
        refunded. Issue the refund manually in Stripe and record it.
        {detailText && <pre className={styles.refundDetail}>{detailText}</pre>}
      </div>
    );
  }
  if (refund.status === 'refunded') {
    return (
      <div className={styles.refundOk} role="status" data-testid="ownership-refund-ok">
        Full Service fee refunded.
      </div>
    );
  }
  return null;
}

function DetailPanel({ id, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refund, setRefund] = useState(null);
  const [announcement, setAnnouncement] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ownershipVerificationAgent.adminGet(id);
      setDetail(data);
    } catch (err) {
      console.error('Ownership detail load failed:', err?.status);
      setError(err?.message || 'Could not load this verification.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setRefund(null);
    setAnnouncement('');
    load();
  }, [load]);

  const handleDecided = (result, action) => {
    if (result && typeof result === 'object') setDetail(result);
    const r = result?.refund || (action === 'reject' && result?.refund_status
      ? { status: result.refund_status, detail: result.refund_detail }
      : null);
    setRefund(r);
    setAnnouncement(
      `Decision saved: ${DECISIONS[action]?.label || action}.` +
        (r?.status === 'failed' ? ' Warning: the refund failed.' : ''),
    );
    if (onChanged) onChanged();
  };

  if (loading && !detail) {
    return <p className={styles.muted} role="status">Loading verification…</p>;
  }
  if (error && !detail) {
    return (
      <div className={styles.errorBox} role="alert">
        {error}{' '}
        <button type="button" className={styles.linkBtn} onClick={load}>Retry</button>
      </div>
    );
  }
  if (!detail) return null;

  const reasons = detail.match_result?.reasons || detail.reasons || [];
  const redFlags = detail.match_result?.red_flags || detail.red_flags || [];
  const events = Array.isArray(detail.events) ? detail.events : [];
  // A stored refund failure stays visible after reload, not just after the click.
  const shownRefund =
    refund || (detail.refund_status && detail.refund_status !== 'none'
      ? { status: detail.refund_status, detail: detail.refund_detail }
      : null);

  return (
    <article className={styles.detail} aria-labelledby={`ov-detail-${detail.id}`} data-testid="ownership-detail">
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">{announcement}</div>
      <header className={styles.detailHead}>
        <div>
          <h3 id={`ov-detail-${detail.id}`} className={styles.detailTitle}>{listingTitle(detail)}</h3>
          <p className={styles.muted} data-testid="ownership-detail-subtitle">
            {detail.house_id == null
              ? `Verification #${detail.id}`
              : [[detail.city, detail.state].filter(Boolean).join(', '), `Listing #${detail.house_id}`]
                  .filter(Boolean)
                  .join(' · ')}
          </p>
        </div>
        <StatusChip status={detail.status} />
      </header>

      <RefundNotice refund={shownRefund} />

      <dl className={styles.facts}>
        <div><dt>Seller</dt><dd>{sellerText(detail)}</dd></div>
        <div><dt>Capacity</dt><dd>{CAPACITY_LABELS[detail.capacity] || detail.capacity || '—'}{detail.capacity_detail ? ` — ${detail.capacity_detail}` : ''}</dd></div>
        <div><dt>Other owners (seller-declared)</dt><dd>{detail.other_owners?.length ? detail.other_owners.join('; ') : 'None'}</dd></div>
        <div><dt>Submitted</dt><dd>{formatDateTime(detail.submitted_at)}</dd></div>
        <div><dt>Attestation</dt><dd>{detail.attestation?.version ? `${detail.attestation.version} · ${formatDateTime(detail.attestation.at)}` : '—'}</dd></div>
        {(detail.poa_confirmed_at || detail.attestation?.poa_confirmed_at) && (
          <div data-testid="ownership-poa-confirmed"><dt>POA confirmed unrevoked / principal living</dt><dd>{formatDateTime(detail.poa_confirmed_at || detail.attestation?.poa_confirmed_at)}</dd></div>
        )}
        {wasPreviouslyPublished(detail) && (
          <div><dt>Listing</dt><dd>Previously published — a rejection removes the badge only</dd></div>
        )}
        {detail.status === 'revoked' && (
          <div data-testid="ownership-revoke-source">
            <dt>Revocation</dt>
            <dd>{REVOKE_SOURCE_LABELS[detail.revoke_source] || 'Revoked (source not recorded)'}</dd>
          </div>
        )}
        {detail.decision_note && <div><dt>Last decision note</dt><dd>{detail.decision_note}</dd></div>}
      </dl>

      {(reasons.length > 0 || redFlags.length > 0) && (
        <section className={styles.flags} aria-label="Why this needs review">
          {reasons.length > 0 && (
            <ul className={styles.reasonList}>
              {reasons.map((r) => <li key={r} className={String(r).startsWith('red_flag:') ? styles.flagItem : styles.reasonItem}>{reasonLabel(r)}</li>)}
            </ul>
          )}
          {redFlags.length > 0 && (
            <ul className={styles.reasonList}>
              {redFlags
                .filter((f) => !reasons.includes(`red_flag:${f}`))
                .map((f) => <li key={f} className={styles.flagItem}>Red flag: {redFlagLabel(f)}</li>)}
            </ul>
          )}
        </section>
      )}

      <div className={styles.split}>
        <section className={styles.splitPane} aria-label="Documents">
          <h4 className={styles.sectionTitle}>Documents</h4>
          <DocumentViewer verificationId={detail.id} documents={detail.documents} />
        </section>
        <section className={styles.splitPane} aria-label="Extracted fields">
          <h4 className={styles.sectionTitle}>Document vs listing</h4>
          <ComparisonTable detail={detail} />
        </section>
      </div>

      <ReviewerChecklist />

      <section aria-label="Decision">
        <h4 className={styles.sectionTitle}>Decision</h4>
        <DecisionPanel detail={detail} onDecided={handleDecided} />
      </section>

      {events.length > 0 && (
        <details className={styles.events}>
          <summary>Audit trail ({events.length})</summary>
          <ol className={styles.eventList}>
            {events.map((ev, i) => (
              <li key={ev.id ?? i}>
                <span className={styles.eventTime}>{formatDateTime(ev.created_at)}</span>{' '}
                <strong>{eventLabel(ev.action)}</strong>
                {ev.from_status || ev.to_status ? ` · ${ev.from_status || '—'} → ${ev.to_status || '—'}` : ''}
                {ev.actor_name ? ` · ${ev.actor_name}` : ev.actor_id == null ? ' · system' : ''}
                {ev.note ? ` — ${ev.note}` : ''}
              </li>
            ))}
          </ol>
        </details>
      )}
    </article>
  );
}

/**
 * @param {object} props
 * @param {number|null} [props.initialCaseId] - a case to open straight away
 *   (the notification email's deep link). The detail loads by id; if the case
 *   isn't in the default "Needs review" queue the filter switches to "All" so
 *   it is also visible in the list.
 */
export default function OwnershipReviewManager({ initialCaseId = null }) {
  const [statusFilter, setStatusFilter] = useState('needs_review');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(initialCaseId);
  // The deep-linked case still to be located in the queue (null once done).
  const pendingCaseRef = useRef(initialCaseId);
  // Which filter the current `items` were loaded for.
  const [itemsFilter, setItemsFilter] = useState(null);
  // Latest publish-gate settings from the card above the queue (null until loaded).
  const [gateSettings, setGateSettings] = useState(null);
  const gateOff = Boolean(gateSettings) && (gateSettings.enabled !== true || gateSettings.env_forced_off === true);
  const filterId = useId();

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ownershipVerificationAgent.adminList({ status: statusFilter });
      setItems(Array.isArray(data) ? data : data?.items || []);
      setItemsFilter(statusFilter);
    } catch (err) {
      console.error('Ownership queue load failed:', err?.status);
      setError(
        err?.status === 403
          ? 'You need admin access to review ownership documents.'
          : err?.message || 'Could not load the review queue.',
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Deep link: once the first queue has loaded, widen the filter to "All" if
  // the case isn't in it (it may already be approved, rejected…). One shot.
  useEffect(() => {
    const pending = pendingCaseRef.current;
    if (pending == null || loading || error || itemsFilter !== statusFilter) return;
    pendingCaseRef.current = null;
    const found = items.some((i) => String(i.id) === String(pending));
    if (!found && statusFilter !== 'all') setStatusFilter('all');
  }, [items, itemsFilter, loading, error, statusFilter]);

  return (
    <div className={styles.manager} data-testid="ownership-review-manager">
      <OwnershipGateSettings onSettingsChange={setGateSettings} />

      {gateOff && (
        <p className={styles.gateOffBanner} role="status" data-testid="ownership-gate-off-banner">
          Ownership review is not required right now. Listings can publish without it.
        </p>
      )}

      <div className={styles.toolbar}>
        <h2 className={styles.heading}>Ownership review</h2>
        <label className={styles.filterLabel} htmlFor={filterId}>Status</label>
        <select
          id={filterId}
          className={styles.filter}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="ownership-status-filter"
        >
          {ADMIN_STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <button type="button" className={styles.secondaryBtn} onClick={loadQueue} disabled={loading}>
          Refresh
        </button>
        <span className={styles.count} aria-live="polite">
          {loading ? 'Loading…' : `${items.length} ${items.length === 1 ? 'item' : 'items'}`}
        </span>
      </div>

      <div className={styles.layout}>
        <section className={styles.queue} aria-label="Review queue">
          {error && (
            <div className={styles.errorBox} role="alert">
              {error}{' '}
              <button type="button" className={styles.linkBtn} onClick={loadQueue}>Retry</button>
            </div>
          )}
          {!error && !loading && items.length === 0 && (
            <p className={styles.empty} data-testid="ownership-queue-empty">Nothing to review here.</p>
          )}
          {!error && items.length > 0 && (
            <ul className={styles.queueList}>
              {items.map((item) => {
                const flags = [...(item.red_flags || [])];
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`${styles.queueItem} ${item.id === selectedId ? styles.queueItemActive : ''}`}
                      onClick={() => setSelectedId(item.id)}
                      aria-current={item.id === selectedId ? 'true' : undefined}
                      data-testid={`ownership-queue-item-${item.id}`}
                    >
                      <span className={styles.queueTop}>
                        <span className={styles.queueAddress}>{listingTitle(item)}</span>
                        <StatusChip status={item.status} />
                      </span>
                      <span className={styles.queueMeta}>
                        {[[item.city, item.state].filter(Boolean).join(', '), sellerText(item)].filter(Boolean).join(' · ')}
                      </span>
                      <span className={styles.queueMeta}>
                        {CAPACITY_LABELS[item.capacity] || item.capacity} · {formatDateTime(item.submitted_at)}
                      </span>
                      {(item.reasons?.length > 0 || flags.length > 0) && (
                        <span className={styles.queueTags}>
                          {(item.reasons || []).filter((r) => !String(r).startsWith('red_flag:')).map((r) => (
                            <span key={r} className={styles.tag}>{String(r).replace(/_/g, ' ')}</span>
                          ))}
                          {flags.map((f) => (
                            <span key={f} className={`${styles.tag} ${styles.tagFlag}`}>{String(f).replace(/_/g, ' ')}</span>
                          ))}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className={styles.detailPane} aria-label="Verification detail">
          {selectedId != null ? (
            <DetailPanel key={selectedId} id={selectedId} onChanged={loadQueue} />
          ) : (
            <p className={styles.empty}>
              {items.length > 0 ? 'Select a verification to review it.' : 'No verification selected.'}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
